import React, { useState } from 'react';
import { Tabs, Descriptions, Table, Tag, Form, Input, Button, Space, message } from 'antd';
import UserSettings from './UserSettings';
import { authService } from '../services/authService';
import { useI18n } from '../hooks/useI18n';

const UserProfilePage: React.FC = () => {
  const storedUser = authService.getStoredUser();
  const currentTenant = authService.getCurrentTenant();
  const [pwdForm] = Form.useForm();
  const [changing, setChanging] = useState(false);
  const { t } = useI18n();
  const contentStyle: React.CSSProperties = { background: '#fff', padding: 16, minHeight: 360, width: '100%' };

  return (
    <div style={{ width: '80%', margin: '20px auto' }}>
      <h2 style={{ margin: '8px 0 16px' }}>{t('profile.title')}</h2>
      <Tabs
        defaultActiveKey="basic"
        items={[
          {
            key: 'basic',
            label: t('profile.basic'),
            children: (
              <div style={contentStyle}>
                <Form layout="vertical">
                  <Form.Item label={t('profile.username')}>
                    <Input disabled value={storedUser?.username || '-'} />
                  </Form.Item>
                  <Form.Item label={t('profile.currentTenant')}>
                    <Input disabled value={currentTenant || '-'} />
                  </Form.Item>
                  <Form.Item label={t('profile.isSuperAdmin')}>
                    <Input disabled value={storedUser?.isSuperAdmin ? t('common.yes') : t('common.no')} />
                  </Form.Item>
                  <Form.Item label={t('profile.tenantCodes')}>
                    <Input disabled value={(storedUser?.tenants?.map(tn => tn.code).join(', ')) || '-'} />
                  </Form.Item>
                </Form>
              </div>
            )
          },
          {
            key: 'tenants',
            label: t('profile.tenants'),
            children: (
              <div style={contentStyle}>
                <Table
                  size="small"
                  rowKey={(r: any) => String(r.id || r.code)}
                  columns={[
                    { title: t('profile.tenantCode'), dataIndex: 'code', key: 'code', width: 160 },
                    { title: t('profile.tenantName'), dataIndex: 'name', key: 'name' },
                    { title: t('profile.role'), dataIndex: 'role', key: 'role', width: 160 },
                    { title: t('profile.tenantAdmin'), dataIndex: 'isTenantAdmin', key: 'isTenantAdmin', width: 120, render: (v: boolean) => v ? <Tag color="green">{t('common.yes')}</Tag> : <Tag>{t('common.no')}</Tag> }
                  ] as any}
                  dataSource={storedUser?.tenants || []}
                  pagination={false}
                />
              </div>
            )
          },
          {
            key: 'preferences',
            label: t('profile.preferences'),
            children: (
              <div style={contentStyle}>
                <UserSettings embedded />
              </div>
            )
          },
          {
            key: 'password',
            label: t('profile.password'),
            children: (
              <div style={contentStyle}>
                <Form
                  form={pwdForm}
                  layout="vertical"
                  onFinish={async (vals) => {
                    if (vals.newPassword !== vals.confirmPassword) {
                      message.error(t('profile.passwordMismatch'));
                      return;
                    }
                    setChanging(true);
                    try {
                      await authService.changePassword(vals.oldPassword, vals.newPassword);
                      message.success(t('profile.passwordChangeSuccessLogout'));
                      await authService.logout();
                      window.location.href = '/login';
                    } catch (e: any) {
                      message.error(t('profile.passwordChangeFailed') + ': ' + (e?.message || ''));
                    } finally {
                      setChanging(false);
                    }
                  }}
                >
                  <Form.Item name="oldPassword" label={t('profile.oldPassword')} rules={[{ required: true, message: t('profile.requiredOldPassword') }]}>
                    <Input.Password placeholder={t('profile.oldPasswordPlaceholder')} autoComplete="current-password" />
                  </Form.Item>
                  <Form.Item name="newPassword" label={t('profile.newPassword')} rules={[{ required: true, message: t('profile.requiredNewPassword') }]}>
                    <Input.Password placeholder={t('profile.newPasswordPlaceholder')} autoComplete="new-password" />
                  </Form.Item>
                  <Form.Item name="confirmPassword" label={t('profile.confirmNewPassword')} rules={[{ required: true, message: t('profile.requiredConfirmPassword') }]}>
                    <Input.Password placeholder={t('profile.confirmNewPasswordPlaceholder')} autoComplete="new-password" />
                  </Form.Item>
                  <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
                    <Space>
                      <Button onClick={() => pwdForm.resetFields()}>{t('common.reset')}</Button>
                      <Button type="primary" htmlType="submit" loading={changing}>{t('profile.changePassword')}</Button>
                    </Space>
                  </Form.Item>
                </Form>
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default UserProfilePage;


